<template>
	<Modal @requestUnmount="$emit('safeToUnmount')" width="sm">
		<template #header>
			<div class="font-semibold text-lg mb-4">{{ $t("Transfer lesson plans") }}...</div>
		</template>
		<template #default="modalActions">
			<!-- Dropdown to select destination collection -->
			<div class="flex flex-col gap-4 mb-4">
				<div>
					{{ $t("from collection") }}...
					<div class="font-semibold italic">{{ sourceCollection?.name }}</div>
				</div>
				<div>
					<div>{{ $t("to collection") }}...</div>
					<select v-model="destinationCollection" class="select select-bordered w-full">
						<option :value="null" disabled></option>
						<option v-for="collection in availableDestinationCollections" :key="collection.id" :value="collection">
							{{ collection.name }}
						</option>
					</select>
				</div>

				<label class="label cursor-pointer flex justify-start items-start gap-3">
					<input type="checkbox" class="checkbox" v-model="removeSourceAfterTransfer" />
					<i18n-t keypath="Remove the collection {name} afterwards" tag="p">
						<template #name>
							<span class="italic font-semibold">"{{ sourceCollection?.name }}"</span>
						</template>
					</i18n-t>
				</label>
			</div>

			<div class="modal-footer flex justify-end gap-1 overflow-hidden">
				<button class="btn btn-ghost" @click="modalActions.hide">{{ $t("Cancel") }}</button>
				<button class="btn btn-primary" @click="confirmTransfer(modalActions)" :disabled="!destinationCollection || processing">
					{{ $t("Confirm") }}
				</button>
			</div>
		</template>
	</Modal>
</template>


<script lang="ts">
import Modal from "@/components/ui/Modal.vue";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { mapStores } from "pinia";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model.ts";
import type { PropType } from 'vue';

export default {
	name: "LessonPlanCollectionTransferModal",

	components: {
		Modal,
	},

	props: {
		sourceCollection: {
			type: Object as PropType<LessonPlanCollection>,
			required: true,
		},
	},

	emits: ["transferred", "safeToUnmount"],

	data() {
		return {
			processing: false,
			destinationCollection: null as LessonPlanCollection | null,
			removeSourceAfterTransfer: true,
		};
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useLessonPlansStore),

		availableDestinationCollections(): LessonPlanCollection[] {
			return this.lessonPlanCollectionsStore.getItems().filter(
				(collection: LessonPlanCollection) => collection.id !== this.sourceCollection?.id
			);
		},
	},

	methods: {

		async confirmTransfer(modalActions: any) {

			if (!this.sourceCollection || !this.destinationCollection) return;

			const plansStore = this.lessonPlansStore;

			this.processing = true;
			try {

				// Execute the merge in the store
				await this.lessonPlanCollectionsStore.mergeCollections(
					this.destinationCollection,
					[ this.sourceCollection ],
					this.removeSourceAfterTransfer
				);

				this.$emit("transferred", {
					sourceCollection: this.sourceCollection,
					destinationCollection: this.destinationCollection,
					removeSource: this.removeSourceAfterTransfer,
				});

				modalActions.hide();

			} catch (error) {
				console.error('Merge error:', error);
				this.$notify({ title: this.$t('Error'), text: this.$t('Failed to transfer lesson plans'), type: 'error' });
			} finally {
				this.processing = false;
			}
		},
	},
};
</script>
